/**
 * Self-scheduling async poller with exponential backoff + jitter, a hard cap
 * on consecutive failures, and an automatic pause while the tab is hidden.
 *
 * LIL-331: a naive `setInterval` poll hits a dead backend at the same rate as a
 * healthy one, with no end. Across many open tabs that's a thundering herd that
 * keeps the service from recovering. This poller slows down on consecutive
 * failures, gives up after a cap (surfacing an error state instead of retrying
 * forever), resets on the first success, and stops polling hidden tabs.
 */

/** A poll attempt either reached a terminal state (`done`) or should keep going. */
export type PollOutcome = 'continue' | 'done';

/**
 * An external gate that can pause polling (e.g. a circuit breaker). While
 * `isPaused()` is true the poller skips attempts; `onChange` notifies it when
 * that may have flipped so it can resume immediately.
 */
export interface PauseSignal {
  isPaused: () => boolean;
  /** Subscribe to pause-state changes. Returns an unsubscribe fn. */
  onChange: (listener: () => void) => () => void;
}

export interface BackoffPollerOptions {
  /** Base delay between successful polls, in ms. */
  intervalMs: number;
  /** Upper bound for the backed-off delay after failures. Default 60_000. */
  maxBackoffMs?: number;
  /** Multiplier applied per consecutive failure. Default 2. */
  backoffFactor?: number;
  /** Fraction of the delay (0..1) added as random jitter to de-sync clients. Default 0.3. */
  jitterRatio?: number;
  /**
   * Consecutive failures tolerated before giving up. After this many the poller
   * stops and calls `onGiveUp`. Default `Infinity` (retry forever, backed off).
   */
  maxConsecutiveFailures?: number;
  /** Pause polling while `document.hidden` is true. Default true. */
  pauseWhenHidden?: boolean;
  /** External gate (e.g. a circuit breaker) that pauses polling while open. */
  pauseSignal?: PauseSignal;
  /** Called on every failed attempt with the error and the running failure count. */
  onError?: (error: unknown, consecutiveFailures: number) => void;
  /** Called once when `maxConsecutiveFailures` is exceeded; the poller is stopped. */
  onGiveUp?: (error: unknown) => void;
}

export interface BackoffPoller {
  /** Stop polling and detach listeners. Idempotent. */
  stop: () => void;
}

/**
 * Delay before the next attempt given the number of consecutive failures so far
 * (0 = the last attempt succeeded → base interval). Exported for testing.
 */
export function computeBackoffDelay(
  consecutiveFailures: number,
  { intervalMs, maxBackoffMs = 60_000, backoffFactor = 2, jitterRatio = 0.3 }: BackoffPollerOptions
): number {
  if (consecutiveFailures <= 0) return intervalMs;
  const raw = intervalMs * backoffFactor ** (consecutiveFailures - 1);
  const capped = Math.min(raw, maxBackoffMs);
  const jitter = capped * jitterRatio * Math.random();
  return capped + jitter;
}

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden;
}

/**
 * Start polling `task` immediately. `task` returns `'done'` to stop on a
 * terminal state, `'continue'` to keep polling, or throws to signal a failed
 * attempt (network/5xx) that triggers backoff.
 */
export function createBackoffPoller(
  task: () => Promise<PollOutcome>,
  options: BackoffPollerOptions
): BackoffPoller {
  const { pauseWhenHidden = true, maxConsecutiveFailures = Infinity, pauseSignal } = options;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let running = false;
  let failures = 0;
  let unsubscribePause: (() => void) | null = null;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  // Paused by a hidden tab or by an external gate (e.g. the circuit breaker).
  const isPaused = () => (pauseWhenHidden && isHidden()) || (pauseSignal?.isPaused() ?? false);

  function stop() {
    if (stopped) return;
    stopped = true;
    clearTimer();
    if (pauseWhenHidden && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibility);
    }
    unsubscribePause?.();
    unsubscribePause = null;
  }

  const schedule = (delay: number) => {
    clearTimer();
    if (stopped) return;
    timer = setTimeout(run, delay);
  };

  const run = async () => {
    if (stopped || running) return;
    // Wait for a resume signal instead of polling while paused.
    if (isPaused()) return;

    running = true;
    try {
      const outcome = await task();
      if (stopped) return;
      failures = 0;
      if (outcome === 'done') {
        stop();
        return;
      }
      schedule(options.intervalMs);
    } catch (error) {
      if (stopped) return;
      failures += 1;
      options.onError?.(error, failures);
      if (failures >= maxConsecutiveFailures) {
        const onGiveUp = options.onGiveUp;
        stop();
        onGiveUp?.(error);
        return;
      }
      schedule(computeBackoffDelay(failures, options));
    } finally {
      running = false;
    }
  };

  // Pause/resume on either gate (tab visibility or the external signal).
  const onPauseStateChange = () => {
    if (stopped) return;
    if (isPaused()) {
      // Cancel any pending attempt; `run` also guards on pause.
      clearTimer();
    } else {
      // Resume immediately rather than waiting out the skipped delay.
      schedule(0);
    }
  };

  function handleVisibility() {
    onPauseStateChange();
  }

  if (pauseWhenHidden && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibility);
  }
  unsubscribePause = pauseSignal?.onChange(onPauseStateChange) ?? null;

  run();

  return { stop };
}
